class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.cartRefreshTimer = null;
    this.cartRefreshInProgress = false;
    this.cartRefreshPending = false;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this)
      );

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // =====================================================
  // DYAMKA - Rafraîchissement automatique du Cart Drawer
  // =====================================================

  scheduleCartRefresh() {
    clearTimeout(this.cartRefreshTimer);

    // Petit délai pour laisser Intelligems terminer l'ajout du cadeau.
    this.cartRefreshTimer = setTimeout(() => {
      this.refreshCartContents();
    }, 350);
  }

  async refreshCartContents() {
    if (this.cartRefreshInProgress) {
      this.cartRefreshPending = true;
      return;
    }

    this.cartRefreshInProgress = true;

    try {
      const root = window.Shopify?.routes?.root || '/';

      const sections = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');

      const response = await fetch(
        `${root}cart?sections=${sections}`,
        { cache: 'no-store' }
      );

      if (!response.ok) {
        throw new Error(`Erreur Shopify : ${response.status}`);
      }

      const sectionsData = await response.json();

      const cartResponse = await fetch(
        `${root}cart.js`,
        { cache: 'no-store' }
      );

      if (!cartResponse.ok) {
        throw new Error(`Erreur panier : ${cartResponse.status}`);
      }

      const cart = await cartResponse.json();

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !sectionsData[section.id]) return;

        sectionElement.innerHTML = this.getSectionInnerHTML(
          sectionsData[section.id],
          section.selector
        );
      });

      this.classList.toggle('is-empty', cart.item_count === 0);

      const drawerInner = this.querySelector('.drawer__inner');

      if (drawerInner) {
        drawerInner.classList.toggle('is-empty', cart.item_count === 0);
      }

      // Le remplacement du HTML recrée l'overlay.
      // On rétablit donc son événement de fermeture.
      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this)
      );

      console.log(
        '[DYAMKA] Cart Drawer actualisé :',
        cart.item_count,
        'article(s)'
      );

    } catch (error) {
      console.error('[DYAMKA] Erreur actualisation panier :', error);
    } finally {
      this.cartRefreshInProgress = false;

      if (this.cartRefreshPending) {
        this.cartRefreshPending = false;
        this.scheduleCartRefresh();
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// =====================================================
// DYAMKA - Détection des modifications du panier Shopify
// Compatible avec les ajouts AJAX effectués par Intelligems
// =====================================================

(function () {
  if (window.DYAMKA_CART_REFRESH_INSTALLED) return;

  window.DYAMKA_CART_REFRESH_INSTALLED = true;

  const nativeFetch = window.fetch;

  window.fetch = function (...args) {
    const request = args[0];

    const url = typeof request === 'string'
      ? request
      : request?.url;

    const method = (
      args[1]?.method ||
      (request instanceof Request ? request.method : 'GET')
    ).toUpperCase();

    const isCartMutation =
      method === 'POST' &&
      typeof url === 'string' &&
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url);

    const result = nativeFetch.apply(this, args);

    if (isCartMutation) {
      result.then((response) => {
        if (!response.ok) return;

        const drawer = document.querySelector('cart-drawer');

        if (drawer) {
          drawer.scheduleCartRefresh();
        }
      }).catch(() => {
        // Ne pas perturber la requête Shopify originale.
      });
    }

    return result;
  };

})();